import React, { memo } from 'react'

import { format } from 'date-fns'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'

import { Thumbnail } from '@/components/common/Attachment/Thumbnail'

import { cleanFileName, getFileExtensionFromName } from '@/helpers/file'
import { getKnowledgeBaseLabel } from '@/helpers/knowledge-base'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useTags } from '@/hooks/tags/useTags'

import type { PotentiallyUnhydratedFile } from '@/types/File'

interface FileDetailsProps {
  file: PotentiallyUnhydratedFile
  asLink?: boolean
  className?: string
  includeDate?: boolean
  subChildren?: React.ReactNode
  linkProps?: Omit<React.ComponentProps<typeof Link>, 'href'>
  onClick?: () => void
}

export const FileDetails: React.FC<FileDetailsProps> = memo(
  ({
    file,
    className,
    asLink = false,
    includeDate = true,
    subChildren,
    linkProps,
    onClick,
  }) => {
    const extension = getFileExtensionFromName(file.name)
    const { activeOrgId } = useActiveOrgId()
    const { data: orgTags } = useTags({ orgId: activeOrgId })
    const label = getKnowledgeBaseLabel(file, orgTags ?? [])
    const fileName = cleanFileName(file.name)
    return (
      <div
        className={twMerge(
          'flex items-center gap-x-2 w-full max-w-full',
          className,
        )}
      >
        <Thumbnail type={extension} className="p-[9px]" />
        <div className="flex flex-col w-full max-w-full line-clamp-1">
          {asLink ? (
            <Link
              href={`/files/${file.id}`}
              {...linkProps}
              className="text-left text-sm font-medium text-primary-900 dark:text-primary-100 line-clamp-1 hover:opacity-60"
            >
              {fileName}
            </Link>
          ) : (
            <button
              className={twMerge(
                'text-left text-sm font-medium text-primary-900 dark:text-primary-100 w-fit line-clamp-1',
                onClick ? 'hover:opacity-70' : '',
              )}
              onClick={onClick}
            >
              {fileName}
            </button>
          )}
          <span className="text-left text-[13px] text-primary-700 dark:text-primary-300 line-clamp-1">
            {subChildren ? (
              subChildren
            ) : (
              <>
                <span className="font-medium">{label}</span>
                {includeDate && (
                  <>
                    &nbsp;{`\u2022`}&nbsp;
                    {format(new Date(file.created_at), 'MMM do, yyyy')}
                  </>
                )}
              </>
            )}
          </span>
        </div>
      </div>
    )
  },
)

FileDetails.displayName = 'KnowledgeBase.FileDetails'
